import type { ExtractPropTypes, PropType, InjectionKey, Ref } from 'vue'

export type TabType = 'card' | 'border-card' | 'line'
export type TabPosition = 'top' | 'right' | 'bottom' | 'left'
export type TabTrigger = 'hover' | 'click'

export interface TabPaneProps {
  label: string
  name: string | number
  disabled?: boolean
  lazy?: boolean
  closable?: boolean
}

export interface TabsContext {
  currentName: Ref<string | number>
  registerPane: (pane: TabPaneProps) => void
  unregisterPane: (name: string | number) => void
  handleClick: (name: string | number) => void
  handleClose: (name: string | number) => void
}

export const tabsContextKey: InjectionKey<TabsContext> = Symbol('tabsContextKey')

export const tabsProps = {
  /**
   * 绑定值，选中选项卡的 name
   */
  modelValue: {
    type: [String, Number],
    default: ''
  },
  /**
   * 风格类型
   */
  type: {
    type: String as PropType<TabType>,
    default: 'line'
  },
  /**
   * 选项卡位置
   */
  tabPosition: {
    type: String as PropType<TabPosition>,
    default: 'top'
  },
  /**
   * 标签的宽度
   */
  tabWidth: {
    type: [String, Number],
    default: ''
  },
  /**
   * 是否可以关闭标签
   */
  closable: {
    type: Boolean,
    default: false
  },
  /**
   * 是否可以添加标签
   */
  addable: {
    type: Boolean,
    default: false
  },
  /**
   * 是否自动适应容器宽度
   */
  stretch: {
    type: Boolean,
    default: false
  },
  /**
   * 触发方式
   */
  trigger: {
    type: String as PropType<TabTrigger>,
    default: 'click'
  },
  /**
   * 是否开启过渡动画
   */
  animated: {
    type: Boolean,
    default: true
  },
  /**
   * 是否延迟渲染标签页内容
   */
  lazy: {
    type: Boolean,
    default: false
  }
} as const

export const tabsEmits = {
  'update:modelValue': (value: string | number) => true,
  'tab-click': (pane: TabPaneProps) => true,
  'tab-change': (name: string | number) => true,
  'tab-remove': (name: string | number) => true,
  'tab-add': () => true
}

export type TabsProps = ExtractPropTypes<typeof tabsProps>
export type TabsEmits = typeof tabsEmits